import React, { useEffect, useRef } from "react";
import {
  useForm,
  Controller,
  SubmitHandler,
  FieldValues,
} from "react-hook-form";
import { Field, Type } from "../configuration";
import Information from "./information";
import { InputFactory } from "./input";

export interface FormBuilderProps {
  name: string;
  fields: Field[];
  onSubmit: SubmitHandler<FieldValues>;
  onCancel?: Function;
}

// TODO add submit animation ? Or in index.tsx show loader before thank you page
export default function FormBuilder({
  name,
  fields,
  onSubmit,
  onCancel,
}: FormBuilderProps) {
  const {
    formState: { isSubmitting, errors },
    handleSubmit,
    control,
    watch,
    unregister,
  } = useForm();

  const watchAllFields = watch();
  const isVisible = (fieldDependency: Field["dependency"]): boolean => {
    if (!fieldDependency) {
      return true;
    }
    return watchAllFields[fieldDependency.name] === fieldDependency.value;
  };

  const prevWatchAllFields = useRef<FieldValues>();
  useEffect(() => {
    if (
      JSON.stringify(prevWatchAllFields.current) ===
      JSON.stringify(watchAllFields)
    ) {
      return;
    }
    fields.forEach((field) => {
      if (
        field.dependency &&
        watchAllFields[field.dependency.name] !== field.dependency.value
      ) {
        unregister(field.id ?? field.name);
      }
    });
    prevWatchAllFields.current = watchAllFields;
  }, [fields, watchAllFields, unregister]);

  return (
    <>
      <form
        name={name}
        className="flex flex-col gap-8 lg:gap-12"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-8 lg:gap-12">
          <h3 className="text-lg font-semibold text-font">
            We'd love to hear your thoughts about your web enrollment
            experience. Your feedback is valuable. It will help us improve our
            website to better serve you.
            <span className="text-sm block mt-3 font-normal">
              *=Required field.
            </span>
          </h3>
          {fields.map((field, i) =>
            field.type === Type.INFORMATION ? (
              <Information key={`${field.type}-${i}`} field={field} />
            ) : (
              isVisible(field.dependency) && (
                <Controller
                  key={`${field.id ?? field.name}-controller`}
                  name={field.id ?? field.name}
                  control={control}
                  rules={field.validation}
                  render={({ field: hookFormField }) => (
                    <InputFactory
                      hookFormField={hookFormField}
                      field={field}
                      error={errors[field.name]?.message?.toString()}
                    />
                  )}
                />
              )
            )
          )}
        </div>
        <div className="flex flex-col gap-5 justify-center w-full text-lg">
          <button
            disabled={isSubmitting}
            type="submit"
            className="border-none py-3 px-10 text-font-cta bg-cta md:hover:bg-cta-hover active:bg-cta-hover focus:bg-cta-hover disabled:bg-cta-disabled disabled:hover:bg-cta-disabled inline-flex justify-center cursor-pointer rounded-full font-semibold whitespace-nowrap mx-auto"
          >
            Submit
          </button>
        </div>
      </form>
      {onCancel && (
        <div className="flex flex-col gap-5 justify-center w-full text-lg">
          <button
            onClick={() => onCancel && onCancel()}
            className="underline my-5"
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}
